/**
 * CaptionsVocabularyPageV2 — Captions Vocabulary list, ported from
 * the prototype's `.screen-captions-vocab` (cms-redesign-mockup.html
 * ~9239+). The dataset is the global canonical-form dictionary that
 * the auto-caption pipeline uses to normalize transcribed text.
 *
 * Layout:
 *   • PageHeader (eyebrow Captions, title 'Vocabulary N terms', sub
 *     copy, coral '+ Add Term').
 *   • Toolbar: category SegmentedControl (All + 6 categories). Term
 *     search lives in the global ⌘K palette — no in-page input.
 *   • LibTable: Term / Category / Mishearings / Active / Actions.
 *     Active is a small Switch-style toggle that PUT-saves per row;
 *     a per-id in-flight set prevents rapid clicks from racing.
 *
 * Add / Edit dialog stays the v1 MUI form. It's a multi-field form
 * with chip-style mishearings, category select, and a notes
 * textarea — restyling it is out of scope for this list-page port.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import {
  deleteCaptionVocabularyTerm,
  fetchCaptionVocabulary,
  saveCaptionVocabularyTerm,
} from '../../../actions/captionsActions';
import {
  PageHeader,
  Button,
  SegmentedControl,
  LibTable,
  IconButton,
  Modal,
  cn,
} from '../../redesign';
import {
  FieldBlock,
  TextInput,
  Textarea,
  Select,
  ToggleSwitch,
} from '../../redesign/edit';

/* ─── Icons ────────────────────────────────────────────────────────── */

const PlusSign = (
  <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>
);

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

const TrashIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

/* ─── Constants ────────────────────────────────────────────────────── */

const CATEGORIES = [
  { value: 'tapping_point', label: 'Tapping point' },
  { value: 'setup_phrase',  label: 'Setup phrase' },
  { value: 'technical',     label: 'Technical' },
  { value: 'brand',         label: 'Brand' },
  { value: 'author_name',   label: 'Author name' },
  { value: 'other',         label: 'Other' },
];

// Mirrors the prototype's tinted chip palette for category labels.
const CATEGORY_TINT = {
  tapping_point: { bg: '#D6EBE3', color: '#5C7E72' },
  setup_phrase:  { bg: '#DCE7F5', color: '#3A4F7A' },
  technical:     { bg: '#E5F0EC', color: '#5C7E72' },
  brand:         { bg: '#FCEAD1', color: '#8C5A1E' },
  author_name:   { bg: '#E58A6A26', color: '#C4715B' },
  other:         { bg: '#FAF7F1', color: '#6B6470' },
};

const blankTerm = () => ({
  canonical_form: '',
  category: 'other',
  known_mishearings: [],
  notes: '',
  is_active: true,
});

function CategoryChip({ category }) {
  const tint = CATEGORY_TINT[category] || CATEGORY_TINT.other;
  const label = CATEGORIES.find((c) => c.value === category)?.label || category;
  return (
    <span
      className="inline-flex items-center font-condensed font-bold uppercase whitespace-nowrap"
      style={{
        fontSize: 10,
        letterSpacing: '0.08em',
        padding: '3px 9px',
        borderRadius: 999,
        background: tint.bg,
        color: tint.color,
      }}
    >
      {label}
    </span>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function CaptionsVocabularyPageV2() {
  const dispatch = useDispatch();
  const vocabulary = useSelector((s) => s.captions.vocabulary);
  const isLoading = vocabulary === null;

  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(blankTerm());
  const [mishearingText, setMishearingText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Per-id in-flight set so rapid clicks on the active Switch don't
  // fire multiple PUTs whose order of resolution determines the
  // final state. Mirrors the v1 page's guard.
  const [togglingIds, setTogglingIds] = useState(() => new Set());

  useEffect(() => {
    dispatch(fetchCaptionVocabulary());
  }, [dispatch]);

  const rows = useMemo(() => {
    if (!Array.isArray(vocabulary)) return [];
    if (categoryFilter === 'all') return vocabulary;
    return vocabulary.filter((t) => t.category === categoryFilter);
  }, [vocabulary, categoryFilter]);

  const counts = useMemo(() => {
    const acc = { all: 0 };
    CATEGORIES.forEach((c) => { acc[c.value] = 0; });
    if (Array.isArray(vocabulary)) {
      for (const t of vocabulary) {
        acc.all += 1;
        if (acc[t.category] != null) acc[t.category] += 1;
      }
    }
    return acc;
  }, [vocabulary]);

  const openCreate = () => {
    setDraft(blankTerm());
    setMishearingText('');
    setDialogOpen(true);
  };

  const openEdit = (term) => {
    setDraft({ ...term });
    setMishearingText((term.known_mishearings ?? []).join(', '));
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const onSave = () => {
    if (submitting) return;
    if (!draft.canonical_form.trim()) {
      Swal.fire({ icon: 'warning', title: 'Canonical form is required.', timer: 1500 });
      return;
    }
    const payload = {
      ...draft,
      canonical_form: draft.canonical_form.trim(),
      known_mishearings: mishearingText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      notes: draft.notes?.trim() || null,
    };
    setSubmitting(true);
    dispatch(
      saveCaptionVocabularyTerm(payload, () => setDialogOpen(false)),
    ).finally(() => setSubmitting(false));
  };

  const onDelete = (term) => {
    Swal.fire({
      title: `Delete "${term.canonical_form}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.value) {
        dispatch(deleteCaptionVocabularyTerm(term.id));
      }
    });
  };

  const onToggleActive = (term) => {
    if (togglingIds.has(term.id)) return;
    setTogglingIds((prev) => {
      const next = new Set(prev);
      next.add(term.id);
      return next;
    });
    dispatch(
      saveCaptionVocabularyTerm(
        { ...term, is_active: !term.is_active },
        () => {
          setTogglingIds((prev) => {
            const next = new Set(prev);
            next.delete(term.id);
            return next;
          });
        },
      ),
    );
  };

  const totalLabel = Array.isArray(vocabulary)
    ? `${vocabulary.length.toLocaleString()} terms`
    : undefined;

  const segmentOptions = [
    { value: 'all', label: 'All', count: counts.all },
    ...CATEGORIES.map((c) => ({ value: c.value, label: c.label, count: counts[c.value] })),
  ];

  return (
    <div className="bg-bg min-h-full">
      <PageHeader
        eyebrow="Captions"
        title="Vocabulary"
        total={totalLabel}
        actions={
          <Button variant="coral" leadingIcon={PlusSign} onClick={openCreate}>
            Add Term
          </Button>
        }
      />

      <div
        className="flex items-center"
        style={{ padding: '0 28px 16px', gap: 12, flexWrap: 'wrap' }}
      >
        <SegmentedControl
          value={categoryFilter}
          onChange={setCategoryFilter}
          options={segmentOptions}
        />
      </div>

      {isLoading ? (
        <div
          className="flex items-center justify-center text-text-muted"
          style={{ height: 256 }}
        >
          Loading vocabulary…
        </div>
      ) : rows.length === 0 ? (
        <div
          className="bg-surface border border-sand text-text-muted italic text-center"
          style={{
            borderRadius: 12,
            padding: '40px 16px',
            fontSize: 13,
            margin: '0 28px 32px',
          }}
        >
          {categoryFilter !== 'all'
            ? `No ${CATEGORIES.find((c) => c.value === categoryFilter)?.label.toLowerCase() ?? categoryFilter} terms.`
            : 'No vocabulary terms yet — add one to get started.'}
        </div>
      ) : (
        <div style={{ padding: '0 28px 32px' }}>
          <LibTable
            fixed
            className="border border-sand"
            style={{ borderRadius: 12, overflow: 'hidden' }}
          >
            <colgroup>
              {/* term */}
              <col style={{ width: '24%' }} />
              {/* category */}
              <col style={{ width: '16%' }} />
              {/* mishearings */}
              <col />
              {/* active toggle */}
              <col style={{ width: 80 }} />
              {/* actions */}
              <col style={{ width: 80 }} />
            </colgroup>
            <LibTable.Head>
              <tr>
                <LibTable.Th>Term</LibTable.Th>
                <LibTable.Th>Category</LibTable.Th>
                <LibTable.Th>Known mishearings</LibTable.Th>
                <LibTable.CenterTh>Active</LibTable.CenterTh>
                <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
              </tr>
            </LibTable.Head>
            <tbody>
              {rows.map((term) => (
                <LibTable.Row key={term.id} inactive={!term.is_active}>
                  <LibTable.Td>
                    <div className="flex flex-col min-w-0" style={{ gap: 2 }}>
                      <span
                        className={cn(
                          'font-semibold truncate',
                          term.is_active ? 'text-navy' : 'text-text-light italic',
                        )}
                        style={{ fontSize: 13.5 }}
                        title={term.canonical_form}
                      >
                        {term.canonical_form}
                      </span>
                      {term.notes && (
                        <span
                          className="text-text-muted italic truncate"
                          style={{ fontSize: 11.5 }}
                          title={term.notes}
                        >
                          {term.notes}
                        </span>
                      )}
                    </div>
                  </LibTable.Td>
                  <LibTable.Td>
                    <CategoryChip category={term.category} />
                  </LibTable.Td>
                  <LibTable.Td>
                    {term.known_mishearings && term.known_mishearings.length > 0 ? (
                      <span className="inline-flex flex-wrap" style={{ gap: 4 }}>
                        {term.known_mishearings.map((m) => (
                          <span
                            key={m}
                            className="font-mono text-text bg-cream border border-sand inline-block"
                            style={{
                              fontSize: 11,
                              padding: '1px 6px',
                              borderRadius: 4,
                            }}
                          >
                            {m}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="text-text-muted italic" style={{ fontSize: 12 }}>—</span>
                    )}
                  </LibTable.Td>
                  <LibTable.CenterTd>
                    <ToggleSwitch
                      checked={!!term.is_active}
                      disabled={togglingIds.has(term.id)}
                      onChange={() => onToggleActive(term)}
                    />
                  </LibTable.CenterTd>
                  <LibTable.Td style={{ textAlign: 'right' }}>
                    <div className="inline-flex" style={{ gap: 4 }}>
                      <IconButton size="sm" aria-label="Edit" onClick={() => openEdit(term)}>
                        {EditIconSvg}
                      </IconButton>
                      <IconButton size="sm" aria-label="Delete" onClick={() => onDelete(term)}>
                        {TrashIconSvg}
                      </IconButton>
                    </div>
                  </LibTable.Td>
                </LibTable.Row>
              ))}
            </tbody>
          </LibTable>
        </div>
      )}

      {/* Add / Edit dialog — redesign-native Modal + form primitives. */}
      <Modal
        open={dialogOpen}
        onClose={closeDialog}
        eyebrow="Captions vocabulary"
        title={draft.id ? 'Edit term' : 'Add term'}
        footer={
          <>
            <Button variant="ghost" onClick={closeDialog}>Cancel</Button>
            <Button variant="primary" onClick={onSave} disabled={submitting}>
              {submitting ? 'Saving…' : 'Save term'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col" style={{ gap: 16 }}>
          <FieldBlock label="Canonical form" required htmlFor="vocab-canonical" noMargin>
            <TextInput
              id="vocab-canonical"
              value={draft.canonical_form}
              onChange={(e) => setDraft({ ...draft, canonical_form: e.target.value })}
              autoFocus
            />
          </FieldBlock>
          <FieldBlock label="Category" htmlFor="vocab-category" noMargin>
            <Select
              id="vocab-category"
              value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </Select>
          </FieldBlock>
          <FieldBlock
            label="Known mishearings"
            hint="comma-separated · e.g. tap, tapped, tapping"
            htmlFor="vocab-mishearings"
            noMargin
          >
            <TextInput
              id="vocab-mishearings"
              value={mishearingText}
              onChange={(e) => setMishearingText(e.target.value)}
            />
          </FieldBlock>
          <FieldBlock label="Notes" htmlFor="vocab-notes" noMargin>
            <Textarea
              id="vocab-notes"
              value={draft.notes || ''}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              rows={2}
            />
          </FieldBlock>
          <ToggleSwitch
            checked={!!draft.is_active}
            onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
            label="Active"
          />
        </div>
      </Modal>
    </div>
  );
}
